import React, { useEffect, useState } from 'react';
import { masterSettingsAPI } from '@/api/api';
import { Plus, Edit, Trash2, Search, Users, Filter, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const Sections = () => {
  const [sections, setSections] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [selectedSemesterFilter, setSelectedSemesterFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    course_id: '',
    semester_id: '',
    capacity: 50
  });

  // Auto-suggest section name (A, B, C, D...)
  const suggestSectionName = (courseId, semesterId) => {
    if (!courseId || !semesterId) return '';
    
    // Get existing sections for this course+semester
    const existingSections = sections.filter(
      s => s.course_id === courseId && s.semester_id === semesterId
    );
    
    // Find next available letter
    const usedNames = existingSections.map(s => s.name.toUpperCase());
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    
    for (const letter of alphabet) {
      if (!usedNames.includes(letter) && !usedNames.includes(`SECTION ${letter}`)) {
        return letter;
      }
    }
    return `${alphabet.length + 1}`; // Fallback to number
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sectionsRes, coursesRes, semestersRes] = await Promise.all([
        masterSettingsAPI.getSections(),
        masterSettingsAPI.getCourses(),
        masterSettingsAPI.getSemesters()
      ]);
      setSections(sectionsRes.data);
      setCourses(coursesRes.data);
      setAllSemesters(semestersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = async (courseId) => {
    setFormData({ ...formData, course_id: courseId, semester_id: '', name: '' });
    if (courseId) {
      try {
        const response = await masterSettingsAPI.getSemesters(courseId);
        setSemesters(response.data);
      } catch (error) {
        console.error('Error fetching semesters:', error);
        setSemesters([]);
      }
    } else {
      setSemesters([]);
    }
  };

  const handleSemesterChange = (semesterId) => {
    const suggestedName = suggestSectionName(formData.course_id, semesterId);
    setFormData({ ...formData, semester_id: semesterId, name: suggestedName });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await masterSettingsAPI.updateSection(currentSection.id, formData);
      } else {
        await masterSettingsAPI.createSection(formData);
      }
      setIsDialogOpen(false);
      setEditMode(false);
      setCurrentSection(null);
      setFormData({ name: '', course_id: '', semester_id: '', capacity: 50 });
      fetchData();
    } catch (error) {
      console.error('Error saving section:', error);
      alert('Failed to save section');
    }
  };

  const handleEdit = (section) => {
    setCurrentSection(section);
    setFormData({
      name: section.name,
      course_id: section.course_id,
      semester_id: section.semester_id,
      capacity: section.capacity
    });
    handleCourseChange(section.course_id);
    setEditMode(true);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        await masterSettingsAPI.deleteSection(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting section:', error);
        alert('Failed to delete section');
      }
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : 'Unknown';
  };

  const getCourseCode = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.code : '-';
  };

  const getSemesterName = (semesterId) => {
    const semester = allSemesters.find(s => s.id === semesterId);
    return semester ? semester.name : 'Unknown';
  };

  const filteredSections = sections.filter(section => {
    const matchesSearch = section.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCourse = selectedCourseFilter === '' || section.course_id === selectedCourseFilter;
    const matchesSemester = selectedSemesterFilter === '' || section.semester_id === selectedSemesterFilter;
    return matchesSearch && matchesCourse && matchesSemester;
  });

  // Calculate statistics
  const totalCapacity = sections.reduce((sum, s) => sum + s.capacity, 0);
  const avgCapacity = sections.length > 0 ? Math.round(totalCapacity / sections.length) : 0;
  const uniqueCourses = new Set(sections.map(s => s.course_id)).size;

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="loading">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="sections-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Sections Management</h1>
          <p className="text-gray-600 mt-1">Manage class sections for courses and semesters</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditMode(false);
            setCurrentSection(null);
            setFormData({ name: '', course_id: '', semester_id: '', capacity: 50 });
            setSemesters([]);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-section-button">
              <Plus size={20} className="mr-2" /> Add Section
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="section-dialog">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Section' : 'Add New Section'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="course_id">Course *</Label>
                <select
                  id="course_id"
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={formData.course_id}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  required
                  data-testid="course-select"
                >
                  <option value="">Select Course</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name} ({course.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="semester_id">Semester *</Label>
                <select
                  id="semester_id"
                  className="w-full h-10 border border-gray-300 rounded-md px-3"
                  value={formData.semester_id}
                  onChange={(e) => handleSemesterChange(e.target.value)}
                  required
                  disabled={!formData.course_id}
                  data-testid="semester-select"
                >
                  <option value="">Select Semester</option>
                  {semesters.map(sem => (
                    <option key={sem.id} value={sem.id}>
                      {sem.name}
                    </option>
                  ))}
                </select>
                {!formData.course_id && (
                  <p className="text-xs text-gray-500 mt-1">Please select a course first</p>
                )}
              </div>

              <div>
                <Label htmlFor="name">Section Name *</Label>
                <div className="flex gap-2">
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                    placeholder="Auto-suggested"
                    required
                    className="uppercase"
                    data-testid="section-name-input"
                  />
                  {formData.course_id && formData.semester_id && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, name: suggestSectionName(formData.course_id, formData.semester_id) })}
                      className="px-3 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 flex items-center gap-1"
                      title="Auto-suggest"
                    >
                      <Sparkles className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Auto-suggested: A, B, C... or enter custom name</p>
              </div>

              <div>
                <Label htmlFor="capacity">Student Capacity *</Label>
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  max="200"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                  required
                  data-testid="capacity-input"
                />
              </div>

              <Button type="submit" className="w-full" data-testid="submit-section-button">
                {editMode ? 'Update Section' : 'Create Section'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <Input
              placeholder="Search sections by name..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-sections-input"
            />
          </div>
          
          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={selectedCourseFilter}
              onChange={(e) => {
                setSelectedCourseFilter(e.target.value);
                setSelectedSemesterFilter('');
              }}
              data-testid="filter-course-select"
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.name} ({course.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={selectedSemesterFilter}
              onChange={(e) => setSelectedSemesterFilter(e.target.value)}
              data-testid="filter-semester-select"
            >
              <option value="">All Semesters</option>
              {allSemesters
                .filter(s => !selectedCourseFilter || s.course_id === selectedCourseFilter)
                .map(sem => (
                  <option key={sem.id} value={sem.id}>
                    {sem.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sections Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="sections-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Semester</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredSections.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <Users size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No sections found. Add your first section to get started.</p>
                  </td>
                </tr>
              ) : (
                filteredSections.map((section) => (
                  <tr key={section.id} className="hover:bg-gray-50" data-testid={`section-row-${section.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <Users size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{section.name}</p>
                          <p className="text-xs text-gray-500">{getCourseCode(section.course_id)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{getCourseName(section.course_id)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{getSemesterName(section.semester_id)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="bg-purple-50 px-3 py-1 rounded-full">
                          <span className="text-sm font-medium text-purple-700">{section.capacity} students</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        section.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {section.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleEdit(section)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Edit"
                          data-testid={`edit-section-${section.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          onClick={() => handleDelete(section.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete"
                          data-testid={`delete-section-${section.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Total Sections</p>
              <p className="text-3xl font-bold text-blue-700 mt-2" data-testid="total-sections-stat">{sections.length}</p>
            </div>
            <Users className="text-blue-400" size={40} />
          </div>
        </div>
        
        <div className="bg-green-50 p-6 rounded-xl border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm font-medium">Active Sections</p>
              <p className="text-3xl font-bold text-green-700 mt-2">{sections.filter(s => s.is_active).length}</p>
            </div>
            <Filter className="text-green-400" size={40} />
          </div>
        </div>
        
        <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Total Capacity</p>
              <p className="text-3xl font-bold text-purple-700 mt-2">{totalCapacity}</p>
            </div>
            <div className="text-purple-400 text-2xl font-bold">👥</div>
          </div>
        </div>
        
        <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-sm font-medium">Avg Capacity</p>
              <p className="text-3xl font-bold text-orange-700 mt-2">{avgCapacity}</p>
            </div>
            <div className="text-orange-400 text-2xl font-bold">📊</div>
          </div>
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-xl border border-indigo-200">
          <h3 className="font-semibold text-gray-800 mb-2">Section Distribution</h3>
          <p className="text-sm text-gray-600">
            Sections across <span className="font-bold text-indigo-600">{uniqueCourses} courses</span>
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
          <h3 className="font-semibold text-gray-800 mb-2">Capacity Range</h3>
          <p className="text-sm text-gray-600">
            From <span className="font-bold text-purple-600">{Math.min(...sections.map(s => s.capacity), 0)}</span> to{' '}
            <span className="font-bold text-purple-600">{Math.max(...sections.map(s => s.capacity), 0)}</span> students
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
          <h3 className="font-semibold text-gray-800 mb-2">Total Student Slots</h3>
          <p className="text-sm text-gray-600">
            Can accommodate up to <span className="font-bold text-green-600">{totalCapacity} students</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sections;
